import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const pub = process.env.PUBLIC_URL || "";
const IMG_WELCOME = `${pub}/images/pillow-oats.png`;
const IMG_CEREMONY = `${pub}/images/Calder.png`;

const RSVP_ENDPOINT = process.env.REACT_APP_RSVP_FORM_ACTION || "";

const DEFAULT_TITLE = "Sofia & Jason";
const PAGE_TITLE = "SOFIA & JASON | EVENT DETAILS & RSVP";

export default function DetailsRsvpPage() {
  const location = useLocation();
  const [formStatus, setFormStatus] = useState("idle");
  const [formMessage, setFormMessage] = useState("");

  useEffect(() => {
    document.title = PAGE_TITLE;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, []);

  useEffect(() => {
    if (location.hash !== "#rsvp") return;
    const id = window.setTimeout(() => {
      document.getElementById("rsvp")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.hash]);

  async function handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    setFormMessage("");

    if (!RSVP_ENDPOINT) {
      setFormStatus("error");
      setFormMessage(
        "RSVP is not connected yet. Add REACT_APP_RSVP_FORM_ACTION to a .env file in the project root (for example your Formspree form URL: https://formspree.io/f/xxxx), then restart the dev server."
      );
      return;
    }

    setFormStatus("submitting");
    const data = new FormData(form);

    try {
      const response = await fetch(RSVP_ENDPOINT, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch {
        /* non-JSON body */
      }

      if (response.ok) {
        setFormStatus("success");
        setFormMessage("Thank you — your response has been recorded.");
        form.reset();
        return;
      }

      setFormStatus("error");
      if (payload.error) {
        setFormMessage(String(payload.error));
      } else if (payload.errors && typeof payload.errors === "object") {
        const first = Object.values(payload.errors).flat()[0];
        setFormMessage(first || "Please check the form and try again.");
      } else {
        setFormMessage("Something went wrong. Please try again.");
      }
    } catch {
      setFormStatus("error");
      setFormMessage("Network error. Please try again.");
    }
  }

  return (
    <main className="pt-28 selection:bg-primary-container selection:text-on-primary md:pt-32">
      {/* Hero Monograph Section */}
      <section className="mb-24 px-6 md:px-20">
        <h1 className="mb-8 font-headline text-6xl uppercase leading-none tracking-tighter text-primary-container md:text-9xl">
          DETAILS &amp; RSVP
        </h1>
        <p className="max-w-xl font-body text-xl italic leading-relaxed opacity-80 md:text-2xl">
          All the fun planned for the weekend below
        </p>
      </section>

      {/* Schedule */}
      <section className="space-y-0">
        {/* Welcome party */}
        <div className="grid w-full grid-cols-1 bg-surface-container-low py-24 md:grid-cols-12 md:py-32">
          <div className="grid grid-cols-1 items-center gap-12 px-6 md:col-span-10 md:col-start-2 md:grid-cols-2 md:px-0">
            <div className="order-2 md:order-1 md:px-0">
              <span className="mb-4 block font-sans text-xs font-bold uppercase tracking-[0.2em] text-tertiary-fixed-dim">
                JUNE 12 / WELCOME
              </span>
              <h2 className="mb-2 font-headline text-5xl uppercase text-primary md:text-7xl">
                WELCOME PARTY
              </h2>
              <p className="mb-6 font-sans text-lg font-extrabold uppercase tracking-[0.2em] text-primary-container md:text-xl">
                Attire: Refined Casual
              </p>
              <div className="space-y-6">
                <p className="max-w-md font-body text-lg leading-relaxed">
                  Join us for the start of the weekend at Pillow and Oats Brewery
                  in Beacon — drinks and dinner from some of our favorite spots
                  across the city.
                </p>
                <div className="border-t-0 pt-8">
                  <span className="block font-headline text-4xl text-primary-container">
                    7PM-10PM
                  </span>
                  <span className="font-sans text-sm uppercase tracking-widest opacity-60">
                    385 Main St, Beacon, NY 12508
                  </span>
                </div>
              </div>
            </div>
            <div className="group relative order-1 flex h-[500px] w-full items-center justify-center overflow-hidden bg-secondary-fixed-dim md:order-2">
              <img
                src={IMG_WELCOME}
                alt="Pillow &amp; Oats Brewing logo"
                className="max-h-[85%] max-w-[85%] object-contain object-center transition-all duration-700"
              />
              <div className="absolute bottom-0 right-0 p-8">
                <span
                  className="material-symbols-outlined text-5xl text-surface opacity-40"
                  aria-hidden
                >
                  local_bar
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Ceremony */}
        <div className="grid w-full grid-cols-1 bg-surface-bright py-24 md:grid-cols-12 md:py-48">
          <div className="grid grid-cols-1 items-center gap-0 md:col-span-11 md:col-start-1 md:grid-cols-12">
            <div className="flex h-[400px] w-full items-center justify-center overflow-hidden bg-surface-container-highest md:col-span-7 md:h-[600px]">
              <img
                src={IMG_CEREMONY}
                alt="Large black Calder stabile sculpture on the lawn at Storm King Art Center"
                className="max-h-full max-w-full object-contain object-center"
              />
            </div>
            <div className="mt-12 px-6 md:col-span-5 md:mt-0 md:px-16">
              <span className="mb-4 block font-sans text-xs font-bold uppercase tracking-[0.2em] text-tertiary-fixed-dim">
                JUNE 13 / UNION
              </span>
              <h2 className="mb-2 font-headline text-5xl uppercase text-primary md:text-7xl">
                CEREMONY &amp; CELEBRATION
              </h2>
              <p className="mb-6 font-sans text-lg font-extrabold uppercase tracking-[0.2em] text-primary-container md:text-xl">
                Attire: Sculptural Chic
              </p>
              <p className="mb-12 font-body text-lg leading-relaxed">
                The main event. Exchange of vows followed by dinner and dancing
                amongst the sculptures at Storm King Art Center.
              </p>
              <div className="border-t-0 pt-8">
                <span className="block font-headline text-4xl text-primary-container">
                  4PM-10PM
                </span>
                <span className="font-sans text-sm uppercase tracking-widest opacity-60">
                  1 Museum Rd, New Windsor
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel & lodging */}
      <section className="grid grid-cols-1 gap-24 bg-surface px-6 py-24 md:grid-cols-2 md:px-20">
        <div>
          <h3 className="mb-12 flex items-center gap-4 font-headline text-4xl uppercase">
            <span className="material-symbols-outlined text-3xl text-primary">
              location_on
            </span>
            Venue Locations
          </h3>
          <div className="space-y-12">
            <div>
              <p className="mb-2 font-sans text-xs font-bold uppercase tracking-[0.2em] text-tertiary-fixed-dim">
                CEREMONY &amp; CELEBRATION
              </p>
              <p className="mb-2 font-headline text-2xl text-primary">
                Storm King Art Center
              </p>
              <p className="font-body opacity-70">
                1 Museum Rd, New Windsor, NY 12553
              </p>
            </div>
            <div>
              <p className="mb-2 font-sans text-xs font-bold uppercase tracking-[0.2em] text-tertiary-fixed-dim">
                WELCOME EVENT
              </p>
              <p className="mb-2 font-headline text-2xl text-primary">
                Pillow &amp; Oats Brewery
              </p>
              <p className="font-body opacity-70">
                385 Main St, Beacon, NY 12508
              </p>
            </div>
          </div>
        </div>
        <div>
          <h3 className="mb-12 flex items-center gap-4 font-headline text-4xl uppercase">
            <span className="material-symbols-outlined text-3xl text-primary">
              hotel
            </span>
            Lodging
          </h3>
          <div className="space-y-12">
            <div>
              <p className="font-body text-xl leading-relaxed opacity-80">
                For guests who are looking to spend the weekend in Hudson Valley,
                we would recommend looking for hotels and airbnb options in
                Beacon, Fishkill and Newburgh. The Bride and Groom will be
                staying in Beacon. There will be a shuttle to/from Storm King
                from Beacon Train Station on the wedding day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RSVP */}
      <section
        className="bg-secondary-fixed-dim px-6 py-24 md:py-40 md:px-20"
        id="rsvp"
      >
        <div className="mx-auto max-w-4xl bg-surface-container-lowest p-12 shadow-[0_40px_80px_rgba(52,48,34,0.08)] md:p-24">
          <div className="mb-16 text-center">
            <h2 className="mb-4 font-headline text-5xl uppercase text-primary md:text-7xl">
              RSVP
            </h2>
            <p className="font-sans text-xs uppercase tracking-[0.3em] opacity-60">
              Kindly respond by April 20th, 2026
            </p>
          </div>

          {formStatus === "success" ? (
            <div className="space-y-8 text-center">
              <p
                className="border border-primary-container/20 bg-surface-container-low px-6 py-8 font-body text-lg text-on-surface"
                role="status"
              >
                {formMessage}
              </p>
              <button
                type="button"
                className="font-sans text-xs font-bold uppercase tracking-[0.2em] text-primary-container underline decoration-primary-container/40 underline-offset-4 hover:text-primary"
                onClick={() => {
                  setFormStatus("idle");
                  setFormMessage("");
                }}
              >
                Submit another response
              </button>
            </div>
          ) : (
            <form className="space-y-12" onSubmit={handleSubmit}>
              <input
                type="hidden"
                name="_subject"
                value="Wedding RSVP — Sofia & Jason"
              />
              {/* Formspree honeypot — leave empty (blocks simple bots) */}
              <input
                type="text"
                name="_gotcha"
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />
              {formStatus === "error" && formMessage ? (
                <p
                  className="border border-error/30 bg-error-container/30 px-4 py-3 font-body text-sm text-on-surface"
                  role="alert"
                >
                  {formMessage}
                </p>
              ) : null}

              <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
                <div className="relative">
                  <label
                    className="mb-2 block font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-primary-container"
                    htmlFor="guest_full_name"
                  >
                    Guest(s) Full Name(s)
                  </label>
                  <input
                    id="guest_full_name"
                    name="guest_full_name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="e.g. Jordan Smith"
                    className="w-full border-b-2 border-l-0 border-r-0 border-t-0 border-outline-variant bg-transparent px-0 py-3 font-body text-lg placeholder:opacity-30 focus:border-primary focus:ring-0"
                  />
                </div>
                <div className="relative">
                  <label
                    className="mb-2 block font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-primary-container"
                    htmlFor="email"
                  >
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="hello@example.com"
                    className="w-full border-b-2 border-l-0 border-r-0 border-t-0 border-outline-variant bg-transparent px-0 py-3 font-body text-lg placeholder:opacity-30 focus:border-primary focus:ring-0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-12 border-y border-outline-variant/30 py-12 md:grid-cols-2">
                <div className="flex flex-col items-center space-y-6 text-center md:items-start md:border-r md:border-outline-variant/30 md:pr-12 md:text-left">
                  <h3 className="font-headline text-2xl uppercase tracking-[0.1em] text-primary md:text-3xl">
                    <span className="block">Welcome</span>
                    <span className="block">Party</span>
                  </h3>
                  <div className="flex justify-center gap-8 md:justify-start">
                    <label className="group flex cursor-pointer items-center gap-3">
                      <input
                        className="h-5 w-5 rounded-none border-outline-variant text-primary-container focus:ring-0"
                        name="welcome_party"
                        type="radio"
                        value="accepts"
                        required
                      />
                      <span className="font-body text-sm uppercase tracking-widest transition-colors group-hover:text-primary">
                        accepts
                      </span>
                    </label>
                    <label className="group flex cursor-pointer items-center gap-3">
                      <input
                        className="h-5 w-5 rounded-none border-outline-variant text-primary-container focus:ring-0"
                        name="welcome_party"
                        type="radio"
                        value="regrets"
                      />
                      <span className="font-body text-sm uppercase tracking-widest transition-colors group-hover:text-primary">
                        regrets
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex flex-col items-center space-y-6 text-center md:items-start md:pl-4 md:text-left">
                  <h3 className="font-headline text-2xl uppercase tracking-[0.1em] text-primary md:text-3xl">
                    WEDDING
                  </h3>
                  <div className="flex justify-center gap-8 md:justify-start">
                    <label className="group flex cursor-pointer items-center gap-3">
                      <input
                        className="h-5 w-5 rounded-none border-outline-variant text-primary-container focus:ring-0"
                        name="wedding_reception"
                        type="radio"
                        value="accepts"
                        required
                      />
                      <span className="font-body text-sm uppercase tracking-widest transition-colors group-hover:text-primary">
                        accepts
                      </span>
                    </label>
                    <label className="group flex cursor-pointer items-center gap-3">
                      <input
                        className="h-5 w-5 rounded-none border-outline-variant text-primary-container focus:ring-0"
                        name="wedding_reception"
                        type="radio"
                        value="regrets"
                      />
                      <span className="font-body text-sm uppercase tracking-widest transition-colors group-hover:text-primary">
                        regrets
                      </span>
                    </label>
                  </div>

                  <div className="w-full space-y-4 pt-8">
                    <p className="block font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-primary-container">
                      Will you need a shuttle to/from Storm King from the Beacon train station?
                    </p>
                    <div className="flex justify-center gap-8 md:justify-start">
                      <label className="group flex cursor-pointer items-center gap-3">
                        <input
                          className="h-5 w-5 rounded-none border-outline-variant text-primary-container focus:ring-0"
                          name="shuttle_needed"
                          type="radio"
                          value="yes"
                          required
                        />
                        <span className="font-body text-sm uppercase tracking-widest transition-colors group-hover:text-primary">
                          YES
                        </span>
                      </label>
                      <label className="group flex cursor-pointer items-center gap-3">
                        <input
                          className="h-5 w-5 rounded-none border-outline-variant text-primary-container focus:ring-0"
                          name="shuttle_needed"
                          type="radio"
                          value="no"
                        />
                        <span className="font-body text-sm uppercase tracking-widest transition-colors group-hover:text-primary">
                          NO
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <label
                  className="mb-2 block font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-primary-container"
                  htmlFor="dietary_requirements"
                >
                  Dietary Requirements
                </label>
                <input
                  id="dietary_requirements"
                  name="dietary_requirements"
                  type="text"
                  placeholder="Allergies or restrictions..."
                  className="w-full border-b-2 border-l-0 border-r-0 border-t-0 border-outline-variant bg-transparent px-0 py-3 font-body text-lg placeholder:opacity-30 focus:border-primary focus:ring-0"
                />
              </div>
              <div className="relative">
                <label
                  className="mb-2 block font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-primary-container"
                  htmlFor="song_request"
                >
                  Song Request
                </label>
                <input
                  id="song_request"
                  name="song_request"
                  type="text"
                  placeholder="Something to keep you on the floor"
                  className="w-full border-b-2 border-l-0 border-r-0 border-t-0 border-outline-variant bg-transparent px-0 py-3 font-body text-lg placeholder:opacity-30 focus:border-primary focus:ring-0"
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === "submitting"}
                className="w-full bg-primary-container py-6 font-sans text-xs font-bold uppercase tracking-[0.4em] text-on-primary transition-all duration-300 hover:bg-primary disabled:cursor-not-allowed disabled:opacity-70"
              >
                {formStatus === "submitting"
                  ? "SENDING…"
                  : "SUBMIT RESPONSE"}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
